import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { GameVariant, VARIANT_SPECS, ACTIVE_VARIANTS } from '@draughts/shared';
import { buildRulesIndexPath, buildRulesPath, isRulesLang, type RulesLang } from '../../seo/routes';
import { variantsPreferredFirst } from '../../rules/order-variants';
import './Rules.css';

interface RulesGamePageProps {
  /**
   * Torna all'INDICE della sezione, non a una variante: questa pagina e' una
   * delle due meta' delle regole, e l'altra sono i tornei.
   */
  onBack: () => void;
  onSelectVariant: (v: GameVariant) => void;
  /**
   * La variante preferita dell'utente, in minuscolo: la sua card sale in cima
   * alla griglia. Arriva come PROP e non dallo store perche' questa pagina la
   * pre-renderizza anche il prerender SSR, dove gli store non si possono
   * toccare — vedi RulesPrerenderRoot.
   */
  preferred?: string;
}

/**
 * Le regole di GIOCO: la raccolta delle ventiquattro varianti. Stava sull'indice
 * della sezione finche' l'indice era questo; da quando le regole sono due —
 * gioco e tornei — l'indice e' un bivio e questa e' una delle sue due porte.
 */
export default function RulesGamePage({
  onBack,
  onSelectVariant,
  preferred,
}: RulesGamePageProps) {
  const { t, i18n } = useTranslation();
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';
  const variants = variantsPreferredFirst(ACTIVE_VARIANTS, preferred);

  return (
    <div className="rl-page">
      <div className="rl-inner">
        {/* breadcrumb: un anello solo, come sulle regole dei tornei — il
            genitore di questa pagina e' l'indice della sezione. */}
        <div className="rl-crumb">
          <a
            href={buildRulesIndexPath(lang)}
            onClick={(e) => { e.preventDefault(); onBack(); }}
          >
            <span aria-hidden="true">←</span> {t('rules.eyebrow')}
          </a>
          <span className="rl-sep">/</span>
          <span className="rl-now">{t('rules.gameRulesHead')}</span>
        </div>

        {/* hero */}
        <div className="rl-card rl-hero rl-hero-3">
          <div className="rl-hero-deco"><div className="rl-hero-king" /></div>
          <div className="rl-hero-center">
            <span className="rl-eyebrow">{t('rules.eyebrow')}</span>
            <h1 className="rl-hero-title">{t('rules.gameTitle')}</h1>
            <p className="rl-hero-sub">{t('rules.gameIntro')}</p>
          </div>
          <div className="rl-hero-chip">
            <span className="rl-chip-ico"><Globe /></span>
            <span className="rl-chip-txt">
              <span className="rl-chip-v">{ACTIVE_VARIANTS.length}</span>
              <span className="rl-chip-l">{t('rules.variantsLabel')}</span>
            </span>
          </div>
        </div>

        {/* all variants */}
        <div className="rl-others">
          <h2 className="rl-section-head">
            {t('rules.gameRulesHead')} <span className="rl-count">({ACTIVE_VARIANTS.length})</span>
          </h2>
          <div className="rl-var-grid">
            {variants.map((v) => {
              const spec = VARIANT_SPECS[v];
              const lower = v.toLowerCase();
              return (
                /*
                 * Link veri, non bottoni: questa griglia e' l'unica uscita
                 * verso le ventiquattro pagine variante, e finche' e' stata
                 * fatta di <button> quelle pagine sono rimaste isole,
                 * raggiungibili solo dal sitemap. Il click normale resta una
                 * navigazione dentro la SPA.
                 */
                <a
                  key={v}
                  className="rl-var"
                  href={buildRulesPath(lang, v)}
                  onClick={(e) => { e.preventDefault(); onSelectVariant(v); }}
                >
                  <span className="rl-vflag" aria-hidden="true">{spec.ui.flagEmoji}</span>
                  <span className="rl-vtxt">
                    <span className="rl-vname">{t(`game.variants.${lower}Short`, lower)}</span>
                    <span className="rl-vmeta">{spec.board.size}×{spec.board.size}</span>
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
